import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, Typography, Row, Col, message, Spin, Avatar, Tag } from 'antd';
import { UserOutlined, LockOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import { DEMO_USERS } from '../types/user';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const LoginPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<string>('');
    const { login } = useUser();

    const handleLogin = async (values: { username: string; password: string }) => {
        setLoading(true);
        try {
            const success = await login(values.username, values.password);
            if (success) {
                message.success('登录成功！');
            } else {
                message.error('用户名或密码错误');
            }
        } catch (error) {
            message.error('登录失败，请稍后再试');
        } finally {
            setLoading(false);
        }
    };

    const handleUserSelect = (username: string) => {
        setSelectedUser(username);
        const user = DEMO_USERS.find(u => u.username === username);
        if (user) {
            form.setFieldsValue({
                username: user.username,
                password: getPasswordForUser(user.username)
            });
        }
    };

    const getPasswordForUser = (username: string): string => {
        const passwordMap: Record<string, string> = {
            'admin': 'admin123',
            'dashboard': 'dashboard123',
            'windfarm-a': 'windfarm123'
        };
        return passwordMap[username] || '';
    };

    const getRoleColor = (role: string): string => {
        switch (role) {
            case 'admin': return 'red';
            case 'dashboard-viewer': return 'blue';
            case 'station-admin': return 'green';
            default: return 'default';
        }
    };

    const getRoleText = (role: string): string => {
        switch (role) {
            case 'admin': return '系统管理员';
            case 'dashboard-viewer': return '数据大屏';
            case 'station-admin': return '站点管理员';
            default: return role;
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <Row gutter={[32, 32]} style={{ width: '100%', maxWidth: '1200px' }}>
                {/* 左侧：系统介绍 */}
                <Col xs={24} lg={12}>
                    <div style={{ color: 'white', padding: '40px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                            <ThunderboltOutlined style={{ fontSize: '48px', marginRight: '16px' }} />
                            <Title level={1} style={{ color: 'white', margin: 0 }}>
                                电力预测AI系统
                            </Title>
                        </div>
                        <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '32px' }}>
                            基于人工智能的新能源发电功率预测平台，为风电、光伏、水电等清洁能源提供精准的功率预测服务。
                        </Paragraph>
                        <div style={{ marginBottom: '24px' }}>
                            <h3 style={{ color: 'white', marginBottom: '16px' }}>✨ 核心功能</h3>
                            <ul style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
                                <li>🎯 高精度功率预测（准确率&gt;94%）</li>
                                <li>📊 实时数据监控与分析</li>
                                <li>🤖 智能模型训练与管理</li>
                                <li>⚡ 多站点统一管理平台</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* 右侧：登录表单 */}
                <Col xs={24} lg={12}>
                    <Card
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                            border: 'none'
                        }}
                        bodyStyle={{ padding: '40px' }}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <Title level={2} style={{ marginBottom: '8px' }}>
                                选择演示用户
                            </Title>
                            <Paragraph type="secondary">
                                请选择一个演示账户体验不同的功能权限
                            </Paragraph>
                        </div>

                        {/* 用户选择卡片 */}
                        <div style={{ marginBottom: '24px' }}>
                            <Row gutter={[16, 16]}>
                                {DEMO_USERS.map(user => (
                                    <Col span={8} key={user.id}>
                                        <Card
                                            size="small"
                                            hoverable
                                            style={{
                                                cursor: 'pointer',
                                                border: selectedUser === user.username ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                                borderRadius: '8px'
                                            }}
                                            onClick={() => handleUserSelect(user.username)}
                                            bodyStyle={{ padding: '16px', textAlign: 'center' }}
                                        >
                                            <Avatar
                                                size={48}
                                                icon={<UserOutlined />}
                                                style={{
                                                    backgroundColor: getRoleColor(user.role),
                                                    marginBottom: '8px'
                                                }}
                                            />
                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                {user.name}
                                            </div>
                                            <Tag color={getRoleColor(user.role)}>
                                                {getRoleText(user.role)}
                                            </Tag>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        <Form
                            form={form}
                            onFinish={handleLogin}
                            layout="vertical"
                            size="large"
                        >
                            <Form.Item
                                name="username"
                                rules={[{ required: true, message: '请输入用户名' }]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="用户名"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: '请输入密码' }]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="密码"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: '16px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={loading}
                                    style={{
                                        height: '48px',
                                        borderRadius: '8px',
                                        fontSize: '16px'
                                    }}
                                >
                                    {loading ? <Spin /> : '登录系统'}
                                </Button>
                            </Form.Item>
                        </Form>

                        {/* 权限说明 */}
                        {selectedUser && (
                            <Card
                                size="small"
                                style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
                            >
                                <Title level={5} style={{ marginBottom: '8px' }}>
                                    {DEMO_USERS.find(u => u.username === selectedUser)?.name} 权限范围
                                </Title>
                                {selectedUser === 'admin' && (
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li>✅ 可以查看所有模块和数据</li>
                                        <li>✅ 管理所有电站的模型和预测</li>
                                        <li>✅ 系统完整管理权限</li>
                                    </ul>
                                )}
                                {selectedUser === 'dashboard' && (
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li>✅ 只能查看仪表板数据</li>
                                        <li>❌ 无法访问其他功能模块</li>
                                    </ul>
                                )}
                                {selectedUser === 'windfarm-a' && (
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li>✅ 查看风电场A的历史数据</li>
                                        <li>✅ 管理风电场A的模型训练</li>
                                        <li>✅ 配置风电场A的功率预测</li>
                                        <li>❌ 无法访问其他电站数据</li>
                                    </ul>
                                )}
                            </Card>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default LoginPage; 